/**
 * Primary-side wellness-check alert (delivery block, Phase 10).
 *
 * When a keyholder has asked FinalKey to check on this user, this banner
 * is the loudest of the three alive signals (the others: replying ALIVE
 * to the SMS, or simply signing in — which the backend cron detects on
 * its own). One tap on "I'm okay" cancels the check immediately.
 *
 * Renders nothing when no check is open, so it can sit permanently at
 * the top of the Dashboard.
 */
import { useFocusEffect } from '@react-navigation/native';
import React, { useCallback, useState } from 'react';
import { View, StyleSheet, TouchableOpacity, Alert } from 'react-native';

import { wellnessService, WellnessStatus } from '../services/wellnessService';
import { createLogger } from '../utils/logger';
import { ms } from '../utils/responsive';

import { AppText as Text } from './AppText';

const logger = createLogger('WellnessCheckBanner');

export const WellnessCheckBanner: React.FC = () => {
  const [status, setStatus] = useState<WellnessStatus | null>(null);
  const [confirming, setConfirming] = useState(false);

  useFocusEffect(
    useCallback(() => {
      let cancelled = false;
      wellnessService
        .getMyStatus()
        .then((data) => {
          if (!cancelled) setStatus(data);
        })
        .catch((error) => {
          // Quietly absent on failure — this banner must never block the
          // Dashboard, and signing in already counts as an alive signal.
          logger.debug('Wellness status unavailable:', error);
        });
      return () => {
        cancelled = true;
      };
    }, []),
  );

  if (!status?.active) return null;

  const dueText = status.releaseDueAt
    ? new Date(status.releaseDueAt).toLocaleDateString()
    : null;

  const handleImOkay = async () => {
    if (confirming) return;
    try {
      setConfirming(true);
      await wellnessService.imAlive();
      setStatus({ active: false });
      Alert.alert(
        "Glad you're okay",
        'The wellness check is cancelled and the person who asked has been told all is well.',
      );
    } catch (error) {
      logger.error("Failed to confirm I'm okay:", error);
      Alert.alert('Error', "We couldn't record that just now. Please try again.");
    } finally {
      setConfirming(false);
    }
  };

  return (
    <View
      style={styles.banner}
      accessibilityRole="alert"
      accessibilityLabel="Someone asked us to check on you"
    >
      <Text style={styles.title}>Someone asked us to check on you</Text>
      <Text style={styles.body}>
        One of your trusted people started a wellness check.
        {dueText
          ? ` If we don't hear from you by ${dueText}, the letters you set up for delivery will be released.`
          : ''}
      </Text>
      <TouchableOpacity
        style={[styles.button, confirming && styles.buttonDisabled]}
        onPress={handleImOkay}
        disabled={confirming}
        accessibilityRole="button"
        accessibilityLabel="I'm okay"
      >
        <Text style={styles.buttonText}>{confirming ? 'One moment…' : "I'm okay"}</Text>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  banner: {
    backgroundColor: '#FF9F5A',
    borderRadius: ms(14),
    marginHorizontal: ms(16),
    marginTop: ms(8),
    padding: ms(16),
  },
  title: {
    fontSize: ms(15, 0.3),
    fontWeight: '700',
    color: '#FFFFFF',
  },
  body: {
    fontSize: ms(13, 0.3),
    lineHeight: ms(19, 0.3),
    color: '#FFFFFF',
    marginTop: ms(4),
  },
  button: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    paddingVertical: ms(11),
    alignItems: 'center',
    marginTop: ms(12),
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  buttonText: {
    color: '#1C2C4F',
    fontSize: ms(15, 0.3),
    fontWeight: '700',
  },
});

export default WellnessCheckBanner;
